// LIBRERIE/IMPORTS
import React, { useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import Select from "react-select";
import { useTranslation } from "react-i18next";

// COMPONENTI
import Pagination from "./Pagination";
import SearchFilters from "./Widgets/WidgetSearch";
//import WidgetNetwork from "./Widgets/WidgetNetwork";
import SubHeaderImmobili from "../Header/subHeader/SubHeaderImmobili";
import ImmobiliGrid from "./ImmobiliGrid";
import ImmobiliList from "./ImmobiliList";

// HOOKS
import { useGetProperties } from "../../hooks/useGetProperties";
import { useGetAgencyDetails } from "../../hooks/useGetAgencyDetails";

// CONFIGURAZIONI
import { AGENCY_ID, IMMOBILI_SHOW_VIEW_TOGGLE, appConfig } from "../../config/appConfig";

// FOGLI DI STILE
import styles from './Immobili.module.css';

const Immobili: React.FC = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [propertiesPerPage] = useState(8);
    const [orderBy, setOrderBy] = useState<string>("insertionDate,desc");
    const [currentView, setCurrentView] = useState<'grid' | 'list'>(appConfig.ui.immobiliPage.defaultView);
    const location = useLocation();
    const { t } = useTranslation();

    const queryParams = new URLSearchParams(location.search);
    const contract = queryParams.getAll('contratto').map(v => v.trim()).filter(v => v !== "").map(v => parseInt(v, 10)).filter(v => !isNaN(v));
    const tipologia = queryParams.getAll('tipologia').map(v => parseInt(v, 10)).filter(v => !isNaN(v));
    const frazione = queryParams.getAll('frazione');
    const provincia = String(queryParams.get('provincia') || '');
    const comuni = queryParams.getAll('comune');
    const maxRooms = Number(queryParams.get('camere_max'));
    const maxBathrooms = Number(queryParams.get('bagni_max'));
    const minPrice = Number(queryParams.get('prezzo_min'));
    const maxPrice = Number(queryParams.get('prezzo_max'));
    const sqmtMin = Number(queryParams.get('mq_min'));
    const sqmtMax = Number(queryParams.get('mq_max'));
    const gardenPrivate = Boolean(queryParams.get('giardino_privato'));
    const terrace = Boolean(queryParams.get('terrazzo'));
    const swimmingPool = Boolean(queryParams.get('piscina'));
    const ref = queryParams.get('riferimento');
    const orderByParts = orderBy.split(",");

    const memoizedFilters = useMemo(() => {

        const filters: any = {};

        if (contract.length) filters.contract = contract;
        if (tipologia.length) filters.type = tipologia;
        if (provincia) filters.provincia = provincia;
        if (comuni.length) filters.comune = comuni;
        if (frazione.length) filters.frazione = frazione;

        if (maxRooms) filters.maxRooms = Number(maxRooms);
        if (maxBathrooms) filters.maxBathrooms = Number(maxBathrooms);
        if (minPrice) filters.minPrice = Number(minPrice);
        if (maxPrice) filters.maxPrice = Number(maxPrice);
        if (sqmtMin) filters.sqmtMin = Number(sqmtMin);
        if (sqmtMax) filters.sqmtMax = Number(sqmtMax);

        if (gardenPrivate === true) filters.gardenPrivate = true;
        if (terrace === true) filters.terrace = true;
        if (swimmingPool === true) filters.swimmingPool = true;

        if (ref) filters.ref = ref;

        return filters;
    }, [
        contract,
        tipologia,
        provincia,
        comuni,
        frazione,
        maxRooms,
        maxBathrooms,
        minPrice,
        maxPrice,
        sqmtMin,
        sqmtMax,
        gardenPrivate,
        terrace,
        swimmingPool,
        ref,
    ]);

    const { properties, totalCount, loading, error } = useGetProperties(AGENCY_ID, {
        limit: 100000000,
        offset: 0,
        withCount: true,
        filters: memoizedFilters,
        orderBy: {
            field: orderByParts[0] || 'insertionDate',
            direction: (orderByParts[1] as 'desc' | 'asc') || 'desc',
        },
    });

    const options = [
        { value: "insertionDate,desc", label: t('propertyList.sortOptions.dateDesc') },
        { value: "insertionDate,asc", label: t('propertyList.sortOptions.dateAsc') },
        { value: "price,desc", label: t('propertyList.sortOptions.priceDesc') },
        { value: "price,asc", label: t('propertyList.sortOptions.priceAsc') },
    ];

    const totalPages = totalCount > 0 ? Math.ceil(totalCount / propertiesPerPage) : Math.ceil(properties.length / propertiesPerPage);

    const startIndex = (currentPage - 1) * propertiesPerPage;
    const currentProperties = useMemo(() => {
        if (!loading && !error) {
            return properties.slice(startIndex, startIndex + propertiesPerPage);
        }
        return [];
    }, [properties, propertiesPerPage, loading, error, startIndex]);


    const { agency } = useGetAgencyDetails(AGENCY_ID);

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            window.scrollTo(0, 0);
        }
    };

    const ViewComponent = currentView === 'list' ? ImmobiliList : ImmobiliGrid;
    const pageTitle = t('propertyList.pageTitle');
    const agencyName = agency?.agencyName ?? t('common.defaultAgencyName');
    const helmetTitle = t('common.pageTitlePattern', { pageTitle: pageTitle, agencyName: agencyName });

    const tipologiaFiltersForHeader = queryParams.getAll('tipologia');
    const filtersForHeader = {
        tipologia: tipologiaFiltersForHeader,
        provincia: queryParams.get('provincia') ? [queryParams.get('provincia')!] : [],
        comune: queryParams.getAll('comune'),
    };

    return (
        <>
            <Helmet>
                <title>{helmetTitle}</title>
            </Helmet>

            <SubHeaderImmobili bodyTitle={t('propertyList.pageTitle')} filters={filtersForHeader} />

            <section className="space-ptb">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-md-6">
                            <div className="section-title mb-3 mb-lg-4">
                                <h3>{loading ? t('common.loading') : `${totalCount} ${t('propertyList.foundLabel')}`}</h3>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-lg-3 mb-5 mb-lg-0 order-1 order-lg-0">
                            <div className="sidebar">
                                <SearchFilters />
                                {/* <WidgetNetwork /> */}
                            </div>
                        </div>

                        {/* Risultati */}
                        <div className="col-lg-9 order-0 order-0 order-lg-1">
                            <div className="property-filter d-sm-flex align-items-center justify-content-between mb-4 flex-wrap py-1">
                                {/* ... Dropdown Ordinamento ... */}
                                <ul className="property-short list-unstyled d-sm-flex mb-0 align-items-center">
                                    <li>
                                        <form className="form-inline">
                                            <div className="form-group d-lg-flex d-block align-items-center">
                                                <label className="justify-content-start mr-2">{t('common.orderBy')}</label>
                                                <div className="short-by">
                                                    <Select
                                                        className="basic-select"
                                                        id="alternativeOrder"
                                                        value={options.find((option) => option.value === orderBy)}
                                                        options={options}
                                                        onChange={(e) => setOrderBy(e?.value as string)}
                                                        isSearchable={false}
                                                        styles={{ container: (base) => ({ ...base, width: '200px' }) }}
                                                        placeholder={t('common.select')}
                                                    />
                                                </div>
                                            </div>
                                        </form>
                                    </li>
                                </ul>

                                {IMMOBILI_SHOW_VIEW_TOGGLE && (
                                    <div className="property-view-toggle pr-2">
                                        <button
                                            type="button"
                                            className={`${styles.viewToggleButton} ${currentView === 'grid' ? styles.active : ''}`}
                                            onClick={() => setCurrentView('grid')}
                                            aria-label={t('propertyList.viewToggle.gridLabel') || 'Visualizzazione Griglia'}
                                            aria-pressed={currentView === 'grid'}
                                            disabled={loading}
                                        >
                                            <i className="fas fa-th" aria-hidden="true"></i>
                                        </button>
                                        <button
                                            type="button"
                                            className={`${styles.viewToggleButton} ${currentView === 'list' ? styles.active : ''}`}
                                            onClick={() => setCurrentView('list')}
                                            aria-label={t('propertyList.viewToggle.listLabel') || 'Visualizzazione Lista'}
                                            aria-pressed={currentView === 'list'}
                                            disabled={loading}
                                        >
                                            <i className="fas fa-list" aria-hidden="true"></i>
                                        </button>
                                    </div>
                                )}

                            </div>

                            {!loading && !error && (
                                <div className="row">
                                    <ViewComponent
                                        properties={currentProperties}
                                        loading={false}
                                        error={null}
                                        basePath="immobile"
                                        labelSold={false}
                                    />
                                </div>
                            )}

                            {!loading && error && (
                                <div className="alert alert-danger">{t('propertyList.errorLoadingProperties')}</div>
                            )}

                            {/* Paginazione */}
                            {totalCount > propertiesPerPage && !error && (
                                <div className="row mt-4">
                                    <div className="col-12">
                                        <Pagination
                                            count={totalCount || 0}
                                            limit={propertiesPerPage}
                                            currentPage={currentPage}
                                            onPageChange={handlePageChange}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default Immobili;